import { Link } from 'react-router-dom';
import { usePublicConfig } from '../PublicShell';
import { LegalLayout, Section, List } from './LegalLayout';

export default function Refunds() {
  const c = usePublicConfig();
  const product = c.company.product;

  return (
    <LegalLayout
      title="Refunds and cancellation"
      updated="11 September 2026"
      intro={<>How billing, cancelling and refunds work for {product}.</>}
    >
      <Section title="Billing">
        <p>
          {product} is billed monthly in advance. Your billing date is the day you first
          subscribed. Text messages are included in the monthly price — there is no
          per-message charge and no overage invoice.
        </p>
      </Section>

      <Section title="Cancelling">
        <p>
          You can cancel at any time by emailing{' '}
          <a href={`mailto:${c.contact.billing}`}>{c.contact.billing}</a>. Cancellation takes
          effect at the end of the month you have already paid for; you keep full access until
          then and are not charged again.
        </p>
        <p>
          Cancelling is not the same as deleting your account. If you also want your data
          removed, delete your organisation from Settings — see below.
        </p>
      </Section>

      <Section title="Refunds">
        <p>We refund in these cases:</p>
        <List
          items={[
            <><strong className="text-ink">Within 14 days of first subscribing</strong> — a full
              refund, no reason needed.</>,
            <><strong className="text-ink">Billed after you cancelled</strong> — refunded in
              full.</>,
            <><strong className="text-ink">A prolonged outage</strong> — if the service is
              unavailable for more than 24 consecutive hours through our fault, a pro-rata
              refund for the affected days on request.</>,
            <><strong className="text-ink">We end your agreement without cause</strong> — the
              unused part of the current month, refunded.</>,
          ]}
        />
        <p>We do not normally refund:</p>
        <List
          items={[
            'Part-months where you cancel mid-cycle — you keep access to the end of the period instead.',
            'Months already used, because you did not get round to using the service.',
            'Accounts suspended for a serious breach of the terms.',
          ]}
        />
        <p>
          If your situation is not on either list, ask. We would rather deal with it sensibly
          than argue about a month&rsquo;s fee.
        </p>
      </Section>

      <Section title="How to request a refund">
        <p>
          Email <a href={`mailto:${c.contact.billing}`}>{c.contact.billing}</a> with your
          organisation name and what happened. We aim to reply within 3 working days. Approved
          refunds go back to the original payment method within 10 working days.
        </p>
      </Section>

      <Section title="Deleting your data">
        <p>
          Deleting your organisation from Settings permanently removes your records and stored
          documents. It is immediate and cannot be undone, and it does not by itself cancel
          billing — email us as well so we stop the subscription. Export anything you need to
          keep before you do it. See the <Link to="/privacy">privacy policy</Link> for retention
          periods.
        </p>
      </Section>

      <Section title="Your statutory rights">
        <p>
          {product} is sold for business use, so the consumer cancellation rights in the
          Consumer Contracts Regulations do not usually apply. If you are a sole trader
          contracting largely outside your trade, they may — and nothing here removes rights you
          have by law.
        </p>
      </Section>
    </LegalLayout>
  );
}
